import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { Account } from "@ca/shared";
import { api, HttpError } from "./api.ts";
export interface PendingWrite {
  key: string;
  accountId: string;
  resource: string;
  createdAt: number;
  sequence: number;
  path: string;
  method: string;
  body: unknown;
  state: "pending" | "blocked";
  error: string | null;
}
interface Store extends DBSchema {
  writes: { key: string; value: PendingWrite; indexes: { account: string } };
  accounts: { key: string; value: Account };
}
export type SyncStatus = "saved" | "synced" | "failed";
export function uuidv7(now = Date.now()): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let stamp = BigInt(now);
  for (let i = 5; i >= 0; i--) {
    bytes[i] = Number(stamp & 255n);
    stamp >>= 8n;
  }
  bytes[6] = ((bytes[6] ?? 0) & 15) | 112;
  bytes[8] = ((bytes[8] ?? 0) & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export class WriteQueue {
  private store: Promise<IDBPDatabase<Store>>;
  private active: string | null = null;
  private flight: Promise<void> | null = null;
  private send: typeof api;
  private onChange: () => void;
  constructor(name = "ca-sync", send: typeof api = api, onChange: () => void = () => {}) {
    this.send = send;
    this.onChange = onChange;
    this.store = openDB<Store>(name, 1, {
      upgrade(db) {
        db.createObjectStore("writes", { keyPath: "key" }).createIndex("account", "accountId");
        db.createObjectStore("accounts", { keyPath: "id" });
      },
    });
  }
  activate(id: string | null) {
    this.active = id;
    this.onChange();
  }
  async cache(me: Account) {
    await (await this.store).put("accounts", me);
  }
  async cached(id: string) {
    return (await this.store).get("accounts", id);
  }
  async list(id = this.active) {
    return id ? (await this.store).getAllFromIndex("writes", "account", id) : [];
  }
  async enqueue(
    write: Omit<PendingWrite, "key" | "createdAt" | "state" | "error" | "sequence">,
    optimistic: Account,
  ) {
    if (write.accountId !== this.active) throw new Error("Tài khoản đã đổi.");
    const db = await this.store;
    const tx = db.transaction(["writes", "accounts"], "readwrite");
    // Chống hai tab cùng tạo thao tác từ một revision cục bộ.
    const existing = await tx.objectStore("writes").index("account").getAll(write.accountId);
    if (existing.some((w) => w.resource === write.resource)) {
      const cached = await tx.objectStore("accounts").get(write.accountId);
      if (cached && cached.profile.rev !== optimistic.profile.rev - 1) {
        tx.abort();
        throw new Error("Cài đặt đang đổi trong tab khác. Tải lại trang.");
      }
    }
    const pending: PendingWrite = {
      ...write,
      key: uuidv7(),
      createdAt: Date.now(),
      sequence: existing.reduce((last, w) => Math.max(last, w.sequence), 0) + 1,
      state: "pending",
      error: null,
    };
    await tx.objectStore("writes").add(pending);
    await tx.objectStore("accounts").put(optimistic);
    await tx.done;
    this.onChange();
    return pending;
  }
  async discard(key: string) {
    await (await this.store).delete("writes", key);
    this.onChange();
  }
  async discardResource(resource: string) {
    for (const write of await this.list())
      if (write.resource === resource) await this.discard(write.key);
  }
  /**
   * Gửi các thay đổi đang chờ. `retryFailed` dùng khi vừa có mạng lại: nếu lượt gửi đang chạy
   * thất bại vì mất mạng thì gửi lại ngay, không chờ timer nền 10 giây.
   */
  async drain(onAccount: (me: Account) => void = () => {}, retryFailed = false) {
    if (this.flight) {
      const accountId = this.active;
      await this.flight;
      if (this.active === accountId) {
        const pending = await this.list();
        const blocked = new Set(
          pending.filter((w) => w.state === "blocked").map((w) => w.resource),
        );
        // A write enqueued after the running drain took its snapshot must not
        // wait for the 10-second background timer. Don't immediately retry a
        // transport failure (unless the network just came back) or spin on a
        // resource blocked by a conflict.
        if (
          pending.some(
            (w) =>
              w.state === "pending" &&
              (retryFailed || w.error === null) &&
              !blocked.has(w.resource),
          )
        )
          await this.drain(onAccount);
      }
      return;
    }
    const run = async () => {
      const accountId = this.active;
      if (!accountId) return;
      const db = await this.store;
      const blocked = new Set<string>();
      const writes = (await this.list(accountId)).sort((a, b) => a.sequence - b.sequence);
      for (const initial of writes) {
        const write = await db.get("writes", initial.key); // Tab khác có thể đã gửi khi đang chờ khoá.
        if (!write) continue;
        if (this.active !== accountId) break;
        if (write.state === "blocked" || blocked.has(write.resource)) {
          blocked.add(write.resource);
          continue;
        }
        if (Date.now() - write.createdAt > 7 * 86400000) {
          await db.put("writes", {
            ...write,
            state: "blocked",
            error: "Thao tác đã quá hạn 7 ngày. Lưu lại sau khi đối chiếu.",
          });
          blocked.add(write.resource);
          continue;
        }
        try {
          const me = Account.parse(
            await this.send(write.path, {
              method: write.method,
              body: write.body,
              key: write.key,
              accountId,
            }),
          );
          const settled = db.transaction(["writes", "accounts"], "readwrite");
          await settled.objectStore("writes").delete(write.key);
          const remaining = await settled.objectStore("writes").index("account").getAll(accountId);
          if (!remaining.length) await settled.objectStore("accounts").put(me);
          await settled.done;
          if (!remaining.length && this.active === accountId) onAccount(me);
        } catch (error) {
          if (
            error instanceof HttpError &&
            error.status >= 400 &&
            error.status < 500 &&
            ![401, 429].includes(error.status)
          ) {
            await db.put("writes", { ...write, state: "blocked", error: error.message });
            blocked.add(write.resource);
          } else {
            await db.put("writes", {
              ...write,
              error: "Chưa gửi được. Bản sửa vẫn được lưu trên máy.",
            });
            break;
          }
        }
      }
      this.onChange();
    };
    this.flight = (
      typeof navigator.locks !== "undefined" ? navigator.locks.request("ca-writes", run) : run()
    ).finally(() => {
      this.flight = null;
    });
    await this.flight;
  }
  async status(): Promise<SyncStatus> {
    const writes = await this.list();
    return writes.some((w) => w.state === "blocked" || w.error !== null)
      ? "failed"
      : writes.length
        ? "saved"
        : "synced";
  }
}
